import { Fragment, useState } from "react";
import { Dialog, DialogPanel, Popover, PopoverButton, PopoverGroup, PopoverPanel, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Link, NavLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const examLinks = [
  { name: "Exams & Prep", href: "/exams" },

  { name: "Certification Tracks", href: "/certifications" },
  { name: "Testing Events", href: "/testing-events" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="bg-gradient-to-b from-(--darkdarkred) to-(--darkred) to-45%">
      <nav
        className="mx-auto flex max-w-[85rem] items-center justify-between p-6 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <a href="/" className="-m-1.5 p-1.5">
            <img
              className="max-h-16 min-h-12 min-w-16 object-scale-down"
              src="/evtcc-logo.png"
              alt="EVTCC Logo"
            />
          </a>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center p-2.5"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon
              className="h-8 w-8 rounded-md border border-white text-white"
              aria-hidden="true"
            />
          </button>
        </div>

        <PopoverGroup className="hidden items-center lg:flex lg:gap-x-4 xl:gap-x-12">
          <NavLink
            to="/"
            className={({ isActive }) =>
              twMerge(
                isActive ? "text-white" : "text-white/80",
                "rounded-lg p-2 text-xl font-semibold leading-6  hover:bg-dark hover:text-white active:text-white",
              )
            }
          >
            Home
          </NavLink>
          <Popover className="relative">
            <PopoverButton className="flex cursor-pointer items-center rounded-lg p-2 text-xl font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white">
              Testing
              <ChevronDownIcon
                className="h-5 w-5 flex-none text-white/80 hover:text-white"
                aria-hidden="true"
              />
            </PopoverButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5">
                <div className="p-4">
                  {examLinks.map((item) => (
                    <div
                      key={item.name}
                      className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm leading-6 hover:bg-gray-200"
                    >
                      <div className="flex-auto">
                        <a
                          href={item.href}
                          className="block font-semibold text-gray-900"
                        >
                          {item.name}
                          <span className="absolute inset-0" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverPanel>
            </Transition>
          </Popover>

          <NavLink
            to="/about"
            className={({ isActive }) =>
              twMerge(
                isActive ? "text-white" : "text-white/80",
                "rounded-lg p-2 text-xl font-semibold leading-6  hover:bg-dark hover:text-white active:text-white",
              )
            }
          >
            About
          </NavLink>
          <NavLink
            to="/faqs"
            className={({ isActive }) =>
              twMerge(
                isActive ? "text-white" : "text-white/80",
                "rounded-lg p-2 text-xl font-semibold leading-6  hover:bg-dark hover:text-white active:text-white",
              )
            }
          >
            FAQ&#8217;s
          </NavLink>
          <NavLink
            to="/resources"
            className={({ isActive }) =>
              twMerge(
                isActive ? "text-white" : "text-white/80",
                "rounded-lg p-2 text-xl font-semibold leading-6  hover:bg-dark hover:text-white active:text-white",
              )
            }
          >
            Links & Downloads
          </NavLink>
          <Link
            to="https://register.evtcc.org"
            className="rounded-lg p-2 text-xl font-semibold leading-6 text-white/80  hover:bg-dark hover:text-white active:text-white"
          >
            Log In
          </Link>
          <a
            href="https://www.facebook.com/profile.php?id=100067387294562"
            onClick={() => setMobileMenuOpen(false)}
          >
            <img
              src="/Facebook_Logo_Primary.png"
              className="size-8"
              alt="Facebook Logo"
            ></img>
          </a>
        </PopoverGroup>
      </nav>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-gradient-to-b from-(--darkdarkred) to-(--darkred) px-6 py-6 lg:max-w-sm lg:ring-1 lg:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href="/" className="-m-1.5 p-1.5">
              <span className="sr-only">EVTCC</span>
              <img
                className="h-16 w-auto"
                src="/evtcc-logo.png"
                alt="EVTCC Logo"
              />
            </a>
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center p-2.5"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Open main menu</span>
              <XMarkIcon
                className="h-8 w-8 rounded-md border border-white text-white"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="flex flex-col items-end space-y-2 py-6">
                <NavLink
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  Home
                </NavLink>
                <Link
                  to="https://register.evtcc.org"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  Log In
                </Link>
                <NavLink
                  to="/exams"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  Exams &amp; Prep
                </NavLink>
                <NavLink
                  to="/certifications"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  Certification Tracks
                </NavLink>

                <NavLink
                  to="/testing-events"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  Testing Events
                </NavLink>
                <NavLink
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  About
                </NavLink>
                <NavLink
                  to="/faqs"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  FAQ&#8217;s
                </NavLink>
                <NavLink
                  to="/resources"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-lg p-2 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white"
                >
                  Resources
                </NavLink>
                <Link
                  to="https://www.facebook.com/profile.php?id=100067387294562"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <img
                    src="/Facebook_Logo_Primary.png"
                    className="size-8"
                    alt="Facebook Logo"
                  ></img>
                </Link>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
